import { z } from 'zod';
// ─── Grammar / spelling check ────────────────────────────────────────────────
export const grammarCheckSchema = z.object({
    text: z.string().min(1).max(20000),
    /**
     * The voice the correction should keep. Validated as a plain string rather
     * than an enum of the package's tones: an unrecognised value falls back to
     * the default on the server, and pinning the list here would mean a shared
     * release every time the package learns a tone.
     */
    tone: z.string().max(32).optional(),
});
// ─── Translation ─────────────────────────────────────────────────────────────
export const translateSchema = z.object({
    text: z.string().min(1).max(20000),
    targetLanguage: z.string().min(1).max(64),
    sourceLanguage: z.string().min(1).max(64).optional(),
    tone: z.string().max(32).optional(),
});
// ─── Generic email composer ──────────────────────────────────────────────────
export const emailRecipientSchema = z.object({
    email: z.string().email().max(320),
    name: z.string().max(255).nullable().optional(),
});
export const emailAttachmentRefSchema = z.object({
    s3Key: z.string().min(1).max(1024),
    fileName: z.string().min(1).max(512),
    mimeType: z.string().min(1).max(255),
});
/**
 * Asking a recipient for a file back.
 *
 * ⚠️ **A field absent from a schema is deleted, not rejected.** `validate`
 * assigns `req.body = result.data` and Zod strips unknown keys, so a field the
 * client sends and the schema does not name never reaches the controller — and
 * nothing anywhere reports it. That is exactly how the vendor email's upload
 * link, its cc-self and its manually-typed recipients were all silently inert:
 * the controller read them, the schema had never been told about them.
 *
 * So this lives beside the two schemas that accept it, and both name it.
 */
export const uploadLinkConfigSchema = z.object({
    folderId: z.string().uuid().nullable().optional(),
    // Registry keys, not MIME types. Unknown keys are dropped server-side rather
    // than refused, so a client built against a newer registry cannot fail a send.
    fileTypes: z.array(z.string().max(32)).max(10).optional(),
    label: z.string().max(255).nullable().optional(),
    // Deliberately no `entityType`/`entityId`: the record a file attaches to is
    // the server's to decide. Accepting it here let a caller aim a guest's upload
    // at any record they could name, checked for shape and nothing else.
});
export const composeEmailSchema = z.object({
    recipients: z.array(emailRecipientSchema).min(1).max(200),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string().min(1).max(100000),
    ccSelf: z.boolean().optional(),
    attachments: z.array(emailAttachmentRefSchema).max(20).optional(),
    context: z.string().max(100).optional(),
    /**
     * Files from the library, named by id.
     *
     * Ids rather than object keys on purpose: the server resolves each one
     * through the file system's own authorizer, so a caller can only attach a
     * file they could already see. A key would have to be guarded by a prefix
     * allowlist instead, and the new library is one prefix holding every
     * client's documents.
     */
    fileIds: z.array(z.string().uuid()).max(50).optional(),
    uploadRequest: uploadLinkConfigSchema.optional(),
});
// ─── Public vendor upload ────────────────────────────────────────────────────
export const vendorUploadCreateSchema = z.object({
    s3Key: z.string().min(1).max(1024),
    fileName: z.string().min(1).max(512),
    mimeType: z.string().min(1).max(255),
    fileSizeBytes: z
        .number()
        .int()
        .min(0)
        .max(100 * 1024 * 1024)
        .optional(),
});
//# sourceMappingURL=messaging.js.map